import React, { Component } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import *  as tenantActions from '../../redux/actions/tenantActions';
import * as resourceAccessActions from '../../redux/actions/resourceAccessActions';
import { withRouter } from 'react-router-dom';
import { recordTenantArrival, withoutForeignTenant } from '../../utils/tenantAccessGuard';
import { loadUser } from 'redux-oidc';
import userManager from '../../services/userManager';
import { store } from '../../redux/store';

export class AutoTenantSelector extends Component {
    constructor(props) {
        super(props);
        this.state = {
            requestedTenantId: this.useQuery().get("tenant")
        };
    }

    useQuery = () => {
        return new URLSearchParams(this.props.location.search);
    }

    // A fresh access token from the SSO session, carrying the account's current groups.
    renewAccess = () => {
        return userManager.signinSilent().then(() => loadUser(store, userManager));
    }

    // Another tenant than the one in "?tenant=" was opened (the account does not have the
    // requested one). Left in the address bar it would be passed on to the opened tenant's
    // sugar, which sends the app straight back to the requested tenant - a reload loop.
    dropForeignTenantFromAddress = () => {
        const { location, history, tenant } = this.props;
        if (!location || !history || typeof history.replace !== 'function') {
            return;
        }
        const search = withoutForeignTenant(location.search, tenant.id);
        if (search !== (location.search || '')) {
            history.replace({ pathname: location.pathname, search, hash: location.hash, state: location.state });
        }
    }

    componentDidMount() {
        // Mounted once per page load. The sugar login page sends the app back with
        // "?tenant=" when the account cannot open that tenant; arriving like that again and
        // again is a reload loop, and the tenant is then not loaded into the iframe at all.
        // "no_access=1" is the sugar side saying so right away.
        const noAccess = this.useQuery().get("no_access") === "1";
        if ((recordTenantArrival(this.state.requestedTenantId) || (noAccess && !!this.state.requestedTenantId))
            && !!this.props.actions.tenantAccessDenied) {
            this.props.actions.tenantAccessDenied(this.state.requestedTenantId);
        }

        if (!!this.props.user && !this.props.user.expired) {
            this.props.actions.getAllAvailableTenants();
        }
    }

    tenantInQuery = (location) => {
        return !!location ? new URLSearchParams(location.search).get("tenant") : null;
    }

    // This component is mounted once for the whole app. After a sign-in it is constructed on
    // "/signin-oidc?code=...", and the callback only then replaces the route with the one the
    // user asked for ("/yerp?tenant=<id>"). A "?tenant=" that appears in the route later on is
    // a request just the same, so it is picked up here instead of opening the default tenant.
    // Returns true when the route asked for a tenant just now.
    pickUpRequestedTenant = (prevProps) => {
        const requested = this.tenantInQuery(this.props.location);
        if (!requested || requested === this.tenantInQuery(prevProps.location)
            || requested === (this.props.tenant || {}).id || requested === this.state.requestedTenantId) {
            return false;
        }
        this.setState({ requestedTenantId: requested });
        if (this.props.availableTenants.length > 0) {
            this.props.actions.selectTenant(requested, this.props.availableTenants, this.renewAccess);
        }
        return true;
    }

    componentDidUpdate(prevProps) {
        // The route asked for a tenant just now: it is being selected already, and nothing
        // below may replace that request with the tenant that happens to be open.
        const pickedUp = this.pickUpRequestedTenant(prevProps);

        if (!!this.props.user && (!prevProps.user || this.props.user.sub !== prevProps.user.sub)) {
            this.props.actions.getAllAvailableTenants();
        }

        if (!pickedUp && this.props.availableTenants.length > prevProps.availableTenants.length
            && (this.props.tenant.id !== this.state.requestedTenantId
            || !this.props.tenant.id)) {
                this.props.actions.selectTenant(this.state.requestedTenantId,
                    this.props.availableTenants, this.renewAccess);
        }

        if (!pickedUp && this.props.tenant.id !== prevProps.tenant.id) {
            this.dropForeignTenantFromAddress();
            this.setState({
                requestedTenantId: this.props.tenant.id
            });
        }

        if (this.props.tenant.host !== prevProps.tenant.host) {
            this.props.actions.updateCustomResourceAccess();
            if (!!prevProps.tenant && !!prevProps.tenant.id) {
                this.props.actions.tenantSessionDeactivated(prevProps.tenant.id);
            }

            if (this.props.tenant.id) {
                this.props.actions.tenantSessionActivated(this.props.tenant.id);
            }
        }

    }

    render() {
        return (
            <div />
        );
    }
}

function mapStateToProps(state) {
    return {
        availableTenants: state.availableTenants,
        tenant: state.tenant,
        user: state.oidc.user
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            readTenant: bindActionCreators(tenantActions.readTenant, dispatch),
            selectTenant: bindActionCreators(tenantActions.selectTenant, dispatch),
            selectDefaultTenant: bindActionCreators(tenantActions.selectDefaultTenant, dispatch),
            updateCustomResourceAccess: bindActionCreators(resourceAccessActions.updateCustomResourceAccess, dispatch),
            getAllAvailableTenants: bindActionCreators(tenantActions.getAllAvailableTenants, dispatch),
            tenantSessionActivated: bindActionCreators(tenantActions.tenantSessionActivated, dispatch),
            tenantSessionDeactivated: bindActionCreators(tenantActions.tenantSessionDeactivated, dispatch),
            tenantAccessDenied: bindActionCreators(tenantActions.tenantAccessDenied, dispatch),
        }
    };
}

export default compose(withTranslation('common'),
    connect(
        mapStateToProps,
        mapDispatchToProps
    ))(withRouter(AutoTenantSelector));
